import { useConfirm } from '@/components/Confirm';
import { Modal } from '@/components/Modal';
/**
 * Sub-spec 11 Plan 4 — custom roles settings.
 *
 *   /settings/team/roles
 *
 * Built-in roles render read-only (badge "built-in"). Tenant custom roles
 * (account_id set) have edit + delete controls. The role editor groups
 * capabilities by namespace prefix (everything before the first '.').
 */
import {
  type RoleRow,
  createCustomRole,
  deleteCustomRole,
  getAllCapabilities,
  getAllRoles,
  updateCustomRole,
} from '@/lib/api';
import { Loader2, Plus, ShieldCheck, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

function groupByNamespace(caps: string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const cap of caps) {
    const ns = cap.includes('.') ? (cap.split('.')[0] as string) : 'general';
    const arr = groups.get(ns) ?? [];
    arr.push(cap);
    groups.set(ns, arr);
  }
  for (const arr of groups.values()) arr.sort();
  return groups;
}

interface RoleEditorProps {
  initial?: RoleRow;
  allCapabilities: string[];
  onCancel: () => void;
  onSaved: (row: RoleRow) => void;
}

function RoleEditor({ initial, allCapabilities, onCancel, onSaved }: RoleEditorProps) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial?.capabilities ?? []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const grouped = useMemo(() => groupByNamespace(allCapabilities), [allCapabilities]);

  function toggle(cap: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const caps = Array.from(selected).sort();
      const row =
        isEdit && initial
          ? await updateCustomRole(initial.id, { name: name.trim(), capabilities: caps })
          : await createCustomRole({
              slug: slug.trim(),
              name: name.trim(),
              capabilities: caps,
            });
      onSaved(row);
    } catch (e) {
      setError((e as Error).message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      onClose={onCancel}
      aria-label={isEdit ? 'Edit role' : 'Create custom role'}
      maxWidth="max-w-3xl"
    >
      <div className="p-6 space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="font-heading text-lg font-medium">
            {isEdit ? `Edit ${initial?.name}` : 'New custom role'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground press"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        {!isEdit && (
          <div>
            <label
              htmlFor="role-slug"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Slug (lower_snake_case)
            </label>
            <input
              id="role-slug"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="kennel_assistant"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        )}

        <div>
          <label
            htmlFor="role-name"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Display name
          </label>
          <input
            id="role-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Kennel Assistant"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        <div>
          <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Capabilities ({selected.size} selected)
          </p>
          <div className="space-y-3">
            {Array.from(grouped.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([ns, caps]) => (
                <fieldset
                  key={ns}
                  className="rounded-lg border border-border bg-secondary/30 px-4 py-3"
                >
                  <legend className="px-2 text-xs font-semibold text-muted-foreground">{ns}</legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {caps.map((cap) => (
                      <label key={cap} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          aria-label={cap}
                          checked={selected.has(cap)}
                          onChange={() => toggle(cap)}
                        />
                        <code className="text-xs">{cap}</code>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={
              saving ||
              name.trim().length === 0 ||
              (!isEdit && !/^[a-z][a-z0-9_]{1,31}$/.test(slug))
            }
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50 press"
          >
            {saving ? 'Saving…' : isEdit ? 'Save role' : 'Create role'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function TeamRoles() {
  const [roles, setRoles] = useState<RoleRow[] | null>(null);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, caps] = await Promise.all([getAllRoles(), getAllCapabilities()]);
      setRoles(list);
      setCapabilities(caps.capabilities);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete(row: RoleRow) {
    const yes = await confirm({
      title: `Delete "${row.name}"?`,
      message: 'Reassign any members using this role first; otherwise the delete will be refused.',
      confirmLabel: 'Delete role',
      danger: true,
    });
    if (!yes) return;
    try {
      await deleteCustomRole(row.id);
      await load();
    } catch (e) {
      setError((e as Error).message ?? 'Delete failed');
    }
  }

  return (
    <div className="space-y-4">
      <ConfirmDialog />
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Roles</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Five built-in roles ship with the platform. Add a custom role if you need a different
            mix of permissions.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          New custom role
        </button>
      </header>

      {error && (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      {loading || roles === null ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" strokeWidth={1.75} /> Loading…
        </p>
      ) : (
        <ul className="space-y-2">
          {roles.map((role) => (
            <li
              key={role.id}
              className="rounded-2xl bg-surface px-5 py-4 ring-1 ring-border elevate"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-medium leading-tight">
                    {role.name}
                    <span className="ml-2 num inline-flex items-center rounded-full bg-secondary px-1.5 py-0.5 text-[10px] ring-hairline">
                      {role.slug}
                    </span>
                    {role.isBuiltin && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                        <ShieldCheck className="size-3" strokeWidth={1.75} />
                        built-in
                      </span>
                    )}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {role.capabilities.length === 1 && role.capabilities[0] === '*'
                      ? 'All capabilities (wildcard)'
                      : `${role.capabilities.length} capabilities`}
                  </p>
                </div>
                {!role.isBuiltin && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditing(role)}
                      className="text-xs underline-offset-2 hover:underline press"
                      aria-label={`Edit ${role.name}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(role)}
                      className="flex items-center gap-1 text-xs text-destructive hover:underline press"
                      aria-label={`Delete ${role.name}`}
                    >
                      <Trash2 className="size-3" strokeWidth={1.75} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showNew && (
        <RoleEditor
          allCapabilities={capabilities}
          onCancel={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            void load();
          }}
        />
      )}
      {editing && (
        <RoleEditor
          initial={editing}
          allCapabilities={capabilities}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}
